import { P, Wrapper, EmailIcon } from '@farewill/ui'
import { COLOR, FONT, GTR } from '@farewill/ui/tokens'
import styled from 'styled-components'

import ActivityTimestamp from 'components/activity-timestamp'
import { ExternalEmail as ExternalEmailType } from 'lib/models/external-email'
import AIIcon from 'components/ai-icon'

const StyledWrapper = styled(Wrapper)`
  display: flex;
  align-items: flex-start;
  margin-bottom: ${GTR.L};
`

const StyledIconCircle = styled.div`
  width: ${GTR.XL};
  height: ${GTR.XL};
  flex-shrink: 0;
  border-radius: 50%;
  margin-right: ${GTR.M};
  background-color: ${COLOR.ACCENT.SECONDARY};
  display: inline-flex;
  align-items: center;
  justify-content: center;
`

const StyledLink = styled.a`
  font-size: ${FONT.SIZE.XS};
`

type ExternalEmailProps = {
  email: ExternalEmailType
}

const getSenderDisplay = (email: ExternalEmailType): string => {
  const { direction, sentByAdminUser, fromAddresses } = email.attributes

  if (direction === 'outbound') {
    if (sentByAdminUser?.name) {
      return sentByAdminUser.name
    }
  }

  return fromAddresses[0] || 'Unknown'
}

export const ExternalEmail = ({
  email,
}: ExternalEmailProps): React.ReactElement => {
  const { direction, subject, emailedAt, externalUrl, summary } =
    email.attributes
  const senderDisplay = getSenderDisplay(email)
  const directionLabel = direction === 'inbound' ? 'Inbound' : 'Outbound'

  return (
    <StyledWrapper>
      <StyledIconCircle>
        <EmailIcon color={COLOR.WHITE} size="M" />
      </StyledIconCircle>
      <Wrapper>
        <P margin={0} size="S">
          {directionLabel} email from <strong>{senderDisplay}</strong>
        </P>
        <ActivityTimestamp datetime={emailedAt} />
        {summary && (
          <Wrapper
            borderRadius="S"
            bordered
            background={COLOR.BACKGROUND.FOG}
            padding="S"
          >
            <P color={COLOR.GREY.DARK} size="S">
              <AIIcon /> <b>AI Summary</b>
            </P>
            <P>
              {subject && (
                <P size="S" color={COLOR.GREY.DARK}>
                  <b>Subject: {subject} </b>
                </P>
              )}
            </P>
            <P size="S">{summary}</P>
          </Wrapper>
        )}
        {!summary && subject && (
          <P margin={['XS', 0, 0]} size="S">
            Subject: {subject}
          </P>
        )}
        {externalUrl && (
          <StyledLink
            href={externalUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            View full email in Front
          </StyledLink>
        )}
      </Wrapper>
    </StyledWrapper>
  )
}
